import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { CaseStatus, KNOWN_ISSUES } from '~/pages/CommercialOps/AriAnomalies/types';

import {
  CaseDetail,
  CasePage,
  CaseQuery,
  CaseScope,
  RollupDimension,
  SuppressionInput,
  TransitionBody,
  getCaseDetail,
  getCaseSummary,
  getRollup,
  listCases,
  listKnownIssues,
  listRules,
  suppressCase,
  transitionCase,
} from '~/services/AriAnomaliesService';

export const ariAnomaliesQueryKeys = {
  all: ['reporting', 'ari-anomalies'] as const,
  cases: () => [...ariAnomaliesQueryKeys.all, 'cases'] as const,
  caseList: (query: CaseQuery) => [...ariAnomaliesQueryKeys.cases(), query] as const,
  summary: () => [...ariAnomaliesQueryKeys.all, 'summary'] as const,
  rollups: () => [...ariAnomaliesQueryKeys.all, 'rollup'] as const,
  rollup: (dimension: RollupDimension, scope: CaseScope) =>
    [...ariAnomaliesQueryKeys.rollups(), dimension, scope] as const,
  details: () => [...ariAnomaliesQueryKeys.all, 'detail'] as const,
  detail: (caseId: string) => [...ariAnomaliesQueryKeys.details(), caseId] as const,
  knownIssues: () => [...ariAnomaliesQueryKeys.all, 'known-issues'] as const,
  rules: () => [...ariAnomaliesQueryKeys.all, 'rules'] as const,
};

export function useGetAriCases(query: CaseQuery) {
  return useQuery({
    queryKey: ariAnomaliesQueryKeys.caseList(query),
    queryFn: ({ signal }) => listCases(query, signal),
    placeholderData: keepPreviousData,
  });
}

export function useGetAriCaseSummary(scope: CaseScope = 'property') {
  return useQuery({
    queryKey: [...ariAnomaliesQueryKeys.summary(), scope],
    queryFn: ({ signal }) => getCaseSummary(signal, scope),
  });
}

export function useGetAriRollup(dimension: RollupDimension, scope: CaseScope = 'property') {
  return useQuery({
    queryKey: ariAnomaliesQueryKeys.rollup(dimension, scope),
    queryFn: ({ signal }) => getRollup(dimension, signal, scope),
    placeholderData: keepPreviousData,
  });
}

export function useGetAriRules() {
  return useQuery({
    queryKey: ariAnomaliesQueryKeys.rules(),
    queryFn: ({ signal }) => listRules(signal),
  });
}

export function useGetAriCaseDetail(caseId: string | null) {
  return useQuery({
    queryKey: ariAnomaliesQueryKeys.detail(caseId ?? ''),
    queryFn: ({ signal }) => getCaseDetail(caseId, signal),
    enabled: !!caseId,
  });
}

export function useGetAriKnownIssues() {
  return useQuery({
    queryKey: ariAnomaliesQueryKeys.knownIssues(),
    queryFn: ({ signal }) => listKnownIssues(signal),
    placeholderData: KNOWN_ISSUES,
  });
}

interface TransitionArgs {
  caseId: string;
  status: CaseStatus;
  body?: TransitionBody;
}

export function useTransitionAriCase() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ caseId, status, body }: TransitionArgs) => transitionCase(caseId, status, body),
    onSuccess: (updated) => {
      queryClient.setQueriesData<CasePage>(
        { queryKey: ariAnomaliesQueryKeys.cases() },
        (page) =>
          page && {
            ...page,
            cases: page.cases.map((c) =>
              c.caseId === updated.caseId ? { ...c, status: updated.status, dismissReason: updated.dismissReason } : c,
            ),
          },
      );
      [ariAnomaliesQueryKeys.cases(), ariAnomaliesQueryKeys.summary(), ariAnomaliesQueryKeys.rollups()].forEach(
        (queryKey) => queryClient.invalidateQueries({ queryKey }),
      );
    },
  });
}

/**
 * Suppressing refetches rather than patching a row.
 *
 * One suppression can cover several cases and the response says only what was created, not
 * which cases it moved — and it moves only the pending and acknowledged ones. Patching would
 * have to guess, and would show a status change on a resolved case that did not get one.
 */
export function useSuppressAriCase() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: SuppressionInput) => suppressCase(input),
    onSuccess: () => {
      [ariAnomaliesQueryKeys.cases(), ariAnomaliesQueryKeys.summary(), ariAnomaliesQueryKeys.rollups()].forEach(
        (queryKey) => queryClient.invalidateQueries({ queryKey }),
      );
    },
  });
}

export type { CaseDetail };
